'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["trackId", "isLoading", "language"];
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect, withRouter, Prompt } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import ImmutablePropTypes from 'react-immutable-proptypes';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import I18n from 'I18n';
import TrackExamPage from '../../components/TrackPage/TrackExamPage';
import { getTrack, getTrackExam, trackIsLoading } from '../../selectors/trackSelectors';
import ApiError from '../../components/Errors/ApiError';
import { TRACK_COMPLETE_URL_FRAGMENT, TRACKS_URL_FRAGMENT } from '../../constants/Navigation';
import UsageTracker from '../../utils/UsageTracker';
import { SCREENS, SUB_SCREENS } from '../../constants/Tracking';
import { DateFormats } from '../../constants/CourseConstants';
import { useExamDeadline, useIsExamAttemptInProgress } from '../../features/exams';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class TrackExamPageContainer extends Component {
  constructor(props) {
    super(props);
    this.handleNavigateToComplete = this.handleNavigateToComplete.bind(this);
    this.handleRouteChange = this.handleRouteChange.bind(this);
    this.state = {
      pageViewTracked: false
    };
  }
  componentDidMount() {
    window.addEventListener('beforeunload', this.handleRouteChange);
    this.maybeTrackPageViewOnce();
  }
  componentDidUpdate() {
    this.maybeTrackPageViewOnce();
  }
  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleRouteChange);
  }
  maybeTrackPageViewOnce() {
    const {
      isLoading,
      examId,
      track
    } = this.props;
    const {
      pageViewTracked
    } = this.state;
    if (!pageViewTracked && examId && !isLoading && track) {
      UsageTracker.track('subpageView', {
        screen: SCREENS.TRACK,
        subscreen: SUB_SCREENS.EXAM,
        examId,
        trackId: track.get('id'),
        trackName: track.get('name'),
        hsSoftwareFilters: track.get('hsSoftwareFilters').join(', '),
        allCategories: track.get('subCategories').join(', ')
      });
      this.setState({
        pageViewTracked: true
      });
    }
  }
  handleNavigateToComplete() {
    const {
      track,
      history: {
        push,
        location
      }
    } = this.props;
    push(Object.assign({}, location, {
      pathname: `/tracks/${track.get('id')}/${TRACK_COMPLETE_URL_FRAGMENT}`
    }));
  }
  handleRouteChange(event) {
    const {
      examAttemptInProgress,
      examDeadline
    } = this.props;
    if (examAttemptInProgress) {
      const formatedDeadline = I18n.moment(examDeadline).format(DateFormats.LONG_DATE_AND_TIME);
      const message = I18n.text('learningCenter.exams.pageLeaveAlert', {
        examDeadline: formatedDeadline
      });
      if (event) {
        event.returnValue = message;
      }
      return message;
    }
    return null;
  }
  render() {
    const {
      examId,
      track,
      isLoading
    } = this.props;
    const pageLeaveWarning = this.handleRouteChange();
    if (isLoading || !track) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true,
        minHeight: 200
      });
    }
    if (track.get('hasError')) {
      return /*#__PURE__*/_jsx(ApiError, {
        className: "track-exam-api-error",
        error: track.get('recentError'),
        marker404: "COURSE_EXAM_VIEW_API_404",
        markerDefault: "COURSE_EXAM_VIEW_API_ERROR"
      });
    }
    if (!examId) {
      return /*#__PURE__*/_jsx(Redirect, {
        to: `${TRACKS_URL_FRAGMENT}/${track.get('id')}`
      });
    }
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Prompt, {
        message: pageLeaveWarning || '',
        when: !!pageLeaveWarning
      }), /*#__PURE__*/_jsx(NavMarker, {
        name: "COURSE_EXAM_VIEW_LOADED"
      }), /*#__PURE__*/_jsx(TrackExamPage, {
        contentId: examId,
        onAdvanceContent: this.handleNavigateToComplete,
        track: track
      })]
    });
  }
}
TrackExamPageContainer.propTypes = {
  examAttemptInProgress: PropTypes.bool,
  examDeadline: PropTypes.number,
  examId: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string
    }).isRequired
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      trackId: PropTypes.string
    }).isRequired
  }).isRequired,
  track: ImmutablePropTypes.map
};
function mapStateToProps(state, ownProps) {
  const track = getTrack(state, ownProps);
  const exam = getTrackExam(state, ownProps);
  const examId = exam && exam.get('id');
  return {
    examId,
    track,
    isLoading: trackIsLoading(state, ownProps)
  };
}
const WithExamAttemptTrackExamPageContainer = _ref => {
  let {
      trackId,
      isLoading,
      language
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isExamAttemptInProgress,
    isExamAttemptInProgressLoading
  } = useIsExamAttemptInProgress(trackId, language);
  const {
    examDeadline,
    examDeadlineLoading
  } = useExamDeadline(trackId, language);
  const isLoadingCombined = isLoading || examDeadlineLoading || isExamAttemptInProgressLoading;
  return /*#__PURE__*/_jsx(TrackExamPageContainer, Object.assign({
    examAttemptInProgress: isExamAttemptInProgress,
    examDeadline: examDeadline,
    isLoading: isLoadingCombined,
    trackId: trackId
  }, props));
};
export default withRouter(connect(mapStateToProps)(WithExamAttemptTrackExamPageContainer));